import { CoreService } from './core.service';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ReportFormInterface, Ship, SubmittedReport } from 'src/types';
import { historyImportKey } from 'src/utils/history-import';
import { mergeReports } from 'src/utils/reports.utils';
import { SKIP_ERROR_LOGGING } from '../interceptors/http-error.interceptor';
import { ReportingService } from './reporting.service';

export interface ReportIssue {
  id: string;
  code: string;
  message: string;
  /** Older API responses omitted severity; those findings are errors. */
  severity?: ReportIssueSeverity;
  /** The finding is available but part of its comparison context is outside this link's scope. */
  contextUnavailable?: boolean;
  /** Reports that should be marked as affected; reports remains the full evidence/context list. */
  affectedReports?: { id: number; reportedTime: string }[];
  reports: { id: number; reportedTime: string }[];
  fuel?: {
    grade: string; blendName?: string; previousRobMT: number; currentRobMT: number;
    expectedRobMT: number; consumptionMT: number; receivedMT: number;
  };
}
export type ReportIssueSeverity = 'error' | 'warning';
interface CheckState {
  enabled: boolean;
  reports: SubmittedReport[];
  issues: ReportIssue[];
  checksStatus: 'ready' | 'checking' | 'unavailable';
  /** Server-authorized action reports covered by a ready canonical check. */
  checkedReportIds?: number[] | null;
  unavailableVoyageUuids?: string[];
  importStatus?: 'active' | 'expired' | 'unavailable';
  nextCursor?: number;
}

export function issueSeverity(issue: Pick<ReportIssue, 'severity'>): ReportIssueSeverity {
  return issue.severity === 'warning' ? 'warning' : 'error';
}

const relatedReportContextNotice = 'Related report details are unavailable with this link. You can still edit and submit this report.';
const legacyContextNotice = 'Related voyage access required.';
// Match the API's static summaries when an older response has only an access notice.
// These labels describe canonical findings without exposing comparison values.
const scopedIssueSummaries = new Map<string, string>([
  ['portIsNull', 'Select a port.'],
  ['inconsistentROBandCons', 'ROB and consumption do not balance.'],
  ['toPortFromPortMismatch', 'The port differs from a related report.'],
  ['mismatchLocationAllFastCastOff', 'The location differs between ALL FAST and CAST OFF.'],
  ['mismatchedReportPairTypes', 'Report types are inconsistent.'],
  ['reportSequenceIsNotLogical', 'The report sequence is not logical.'],
  ['cospNotFollowedByEosp', 'COSP is missing a following EOSP report.'],
  ['eospMissingPrecedingCosp', 'EOSP is missing a preceding COSP report.'],
  ['noonAtBerthNotPrecededByAllFast', 'A noon report at berth requires a preceding ALL FAST report.'],
  ['cargoDidNotIncreaseAfterLoading', 'Cargo did not increase after loading.'],
  ['cargoDidNotDecreaseAfterDischarging', 'Cargo did not decrease after discharging.'],
  ['cargoIncreasedWithoutLoadingActivity', 'Cargo increased without loading activity.'],
  ['cargoDecreasedWithoutDischargingActivity', 'Cargo decreased without discharging activity.'],
]);

function issueContextUnavailable(issue: ReportIssue): boolean {
  return !!issue.contextUnavailable || issue.message === legacyContextNotice || issue.message.endsWith(relatedReportContextNotice);
}

function scopedIssueMessage(issue: ReportIssue): string {
  const message = issue.message === legacyContextNotice ? '' : issue.message.replace(relatedReportContextNotice, '').trim();
  return message || scopedIssueSummaries.get(issue.code) || 'Review this saved report.';
}

export function issueText(issue: ReportIssue): string {
  const label = issueSeverity(issue) === 'warning' ? 'Warning' : 'Error';
  const contextUnavailable = issueContextUnavailable(issue);
  const message = contextUnavailable ? scopedIssueMessage(issue) : issue.message;
  if (!issue.fuel || contextUnavailable) return `${label}: ${message}`;
  const fuel = issue.fuel;
  const mt = (value: number) => new Intl.NumberFormat('en-GB', { maximumFractionDigits: 3 }).format(value);
  const dates = issue.reports.map(report => new Date(report.reportedTime).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
  })).join(' – ');
  return `${label}: ${fuel.grade.toUpperCase()}${fuel.blendName ? ' · ' + fuel.blendName : ''} balance mismatch. ` +
    `${dates} UTC. ` +
    `ROB ${mt(fuel.previousRobMT)} → ${mt(fuel.currentRobMT)} mt; received ${mt(fuel.receivedMT)} mt; used ${mt(fuel.consumptionMT)} mt.`;
}

function mergeIssueViews(issues: ReportIssue[]): ReportIssue[] {
  const byId = new Map<string, ReportIssue>();
  const mergeRefs = (left: ReportIssue['reports'], right: ReportIssue['reports']) =>
    [...new Map([...left, ...right].map(ref => [ref.id, ref])).values()];
  for (const issue of issues) {
    const previous = byId.get(issue.id);
    if (!previous) { byId.set(issue.id, issue); continue; }
    const redacted = (finding: ReportIssue) => finding.contextUnavailable || finding.message === 'Related voyage access required.';
    const preferred = redacted(issue) && !redacted(previous) ? previous : issue;
    const affectedReports = mergeRefs(previous.affectedReports ?? [], issue.affectedReports ?? []);
    byId.set(issue.id, {
      ...preferred,
      reports: mergeRefs(previous.reports, issue.reports),
      ...(affectedReports.length ? { affectedReports } : {}),
    });
  }
  return [...byId.values()];
}

@Injectable({ providedIn: 'root' })
export class ReportChecksService {
  readonly changed = new Subject<void>();
  readonly status = new BehaviorSubject<'ready' | 'checking' | 'unavailable' | 'paused' | 'disabled'>('disabled');
  readonly retry = new Subject<void>();
  accessNotice = '';
  private notifiedImport = '';
  private ship?: Ship;
  private dismissedNotice = '';
  private epoch = 0;
  private issues: ReportIssue[] = [];
  private latest = new Map<string, ReportFormInterface>();
  private pages = new Map<string, { etag: string; state: CheckState }>();
  private legacyImports = new Map<string, number>();

  constructor(private http: HttpClient, private reports: ReportingService, private core: CoreService) {}

  open(ship: Ship) {
    this.epoch++;
    if (this.ship?.uuid !== ship.uuid) this.reset();
    this.ship = ship;
  }

  clear() {
    if (this.ship) sessionStorage.removeItem(historyImportKey(this.ship.uuid));
    this.ship = undefined;
    this.reset();
  }

  private reset() {
    this.epoch++;
    this.notifiedImport = '';
    this.issues = [];
    this.latest.clear();
    this.pages.clear();
    this.legacyImports.clear();
    this.accessNotice = this.dismissedNotice = '';
    this.status.next('disabled');
    this.changed.next();
  }

  saved(report: ReportFormInterface) {
    this.epoch++;
    if (report.id) this.latest.set(String(report.id), report);
    if (report.serverVersion || report.reportAccessToken || report.issueAccessToken || this.status.value !== 'disabled') this.status.next('checking');
    this.pages.clear();
    this.changed.next();
  }

  dismissNotice() { this.dismissedNotice = this.accessNotice; this.accessNotice = ''; }
  get stale() { return this.status.value === 'paused' || this.status.value === 'unavailable' || this.status.value === 'checking'; }
  errors(id: string | number | undefined) {
    return this.issues.filter(issue => (issue.affectedReports ?? issue.reports).some(r => String(r.id) === String(id)));
  }
  /** Returns the highest severity finding attached to a report, if any. */
  severity(id: string | number | undefined): ReportIssueSeverity | undefined {
    const findings = this.errors(id);
    if (findings.some(issue => issueSeverity(issue) === 'error')) return 'error';
    return findings.length ? 'warning' : undefined;
  }
  tooltip(id: string | number | undefined) {
    const issues = this.errors(id);
    const findings = issues.map(issueText).join('\n');
    return issues.some(issueContextUnavailable) ? `${findings}\n\nDetails: ${relatedReportContextNotice}` : findings;
  }
  savedReport(id: string | number | undefined) { return this.latest.get(String(id)); }

  async refresh(ship: Ship): Promise<void> {
    const epoch = this.epoch;
    try {
      const voyageUuids = await firstValueFrom(this.reports.getVoyageUUIDs(ship.imo));
      const local = await firstValueFrom(this.reports.getReports(ship.imo));
      const reportAccessTokens = local.flatMap(r => r.reportAccessToken ? [r.reportAccessToken] : []);
      const issueProofs = new Map<number, string>();
      for (const report of local) {
        const id = Number(report.id);
        if (Number.isSafeInteger(id) && id > 0 && report.issueAccessToken?.startsWith(`${id}.`))
          issueProofs.set(id, report.issueAccessToken);
      }
      const issueReportIds = [...issueProofs.keys()];
      const issueAccessTokens = [...issueProofs.values()];
      const issueBatches: string[][] = [];
      for (let i = 0; i < issueAccessTokens.length; i += 2000) issueBatches.push(issueAccessTokens.slice(i, i + 2000));
      const importToken = sessionStorage.getItem(historyImportKey(ship.uuid)) || undefined;
      if (epoch !== this.epoch) return;
      if (!voyageUuids.length && !reportAccessTokens.length && !importToken && !issueReportIds.length) {
        if (this.status.value !== 'disabled') this.status.next('paused');
        return;
      }
      const incoming: SubmittedReport[] = [];
      const issues: ReportIssue[] = [];
      const unavailableVoyages = new Set<string>();
      const checkedReportIds = new Set<string>();
      let authoritativeCoverage = true;
      const usedPages = new Set<string>();
      let changed = false;
      let cursor: number | undefined;
      let issueBatch = 0;
      let checksStatus: CheckState['checksStatus'] | 'paused' = 'ready';
      do {
        const readScope = issueBatch === 0 || cursor != null;
        const body = {
          voyageUuids: readScope ? voyageUuids : [],
          reportAccessTokens: readScope ? reportAccessTokens : [],
          ...(readScope && importToken ? { importToken } : {}),
          ...(issueBatches[issueBatch]?.length ? { issueAccessTokens: issueBatches[issueBatch] } : {}),
          ...(cursor ? { cursor } : {}),
        };
        const key = JSON.stringify(body);
        const cached = this.pages.get(key);
        usedPages.add(key);
        let state: CheckState;
        try {
          const response = await firstValueFrom(this.http.post<CheckState>(`${environment.siglarApi.url}/api/v1/webform/state`, body, {
            headers: { Authorization: `Vessel ${ship.uuid}`, ...(cached?.etag ? { 'If-None-Match': cached.etag } : {}) },
            context: new HttpContext().set(SKIP_ERROR_LOGGING, true), observe: 'response',
          }));
          state = response.body!;
          changed = true;
          if (epoch !== this.epoch) return;
          this.pages.set(key, { etag: response.headers.get('ETag') || '', state });
        } catch (error) {
          if ((error as HttpErrorResponse).status !== 304 || !cached) throw error;
          state = cached.state;
        }
        if (epoch !== this.epoch) return;
        if (!state.enabled) {
          this.status.next('disabled');
          for (const voyage of voyageUuids) {
            // Preserve the production hourly import cadence when scoped checks are disabled.
            if (Date.now() - (this.legacyImports.get(voyage) ?? 0) < 3_600_000) continue;
            try {
              const response = await firstValueFrom(this.reports.loadSubmittedReports(voyage));
              if (epoch !== this.epoch) return;
              await this.reports.mergeSavedReports(ship.imo, response.data.filter(r => r.imo === ship.imo));
              this.legacyImports.set(voyage, Date.now());
            } catch (error) {
              if (epoch !== this.epoch) return;
              if (![400, 404].includes((error as HttpErrorResponse).status)) throw error;
              await this.reports.forgetVoyages(ship.imo, [voyage]);
            }
          }
          return;
        }
        if (state.importStatus === 'expired' || state.importStatus === 'unavailable') {
          sessionStorage.removeItem(historyImportKey(ship.uuid));
          this.pages.delete(key);
          const notice = 'Import access ended. You can still edit and submit downloaded reports.';
          this.accessNotice = notice === this.dismissedNotice ? '' : notice;
        } else if (state.importStatus === 'active') {
          this.accessNotice = this.dismissedNotice = '';
        }
        incoming.push(...state.reports.filter(r => r.imo === ship.imo));
        if (Array.isArray(state.checkedReportIds)) state.checkedReportIds.forEach(id => checkedReportIds.add(String(id)));
        else authoritativeCoverage = false;
        issues.push(...state.issues);
        state.unavailableVoyageUuids?.forEach(id => unavailableVoyages.add(id));
        if (state.checksStatus === 'unavailable' || checksStatus === 'ready') checksStatus = state.checksStatus;
        if (state.nextCursor != null && state.nextCursor <= (cursor || 0)) throw new Error('Invalid report page');
        cursor = state.nextCursor;
        issueBatch++;
      } while (cursor != null || issueBatch < issueBatches.length);

      for (const key of this.pages.keys()) if (!usedPages.has(key)) this.pages.delete(key);
      if (checksStatus !== 'unavailable' && !incoming.length && !issueReportIds.length &&
        (reportAccessTokens.length || unavailableVoyages.size || this.accessNotice)) checksStatus = 'paused';
      for (const report of incoming) {
        const saved = this.latest.get(String(report.id));
        if (!saved || saved.serverVersion !== report.version || saved.locked !== report.locked)
          this.latest.set(String(report.id), mergeReports([report], [])[0]);
      }
      // A ready response contains fresh canonical findings for each authorized action report.
      // Missing comparison context must not keep a corrected report marked indefinitely.
      // Retain the last finding only for action reports outside the refreshed scope.
      const ids = new Set([...incoming.map(r => String(r.id)),
        ...(authoritativeCoverage ? checkedReportIds : issueReportIds.map(String))]);
      if (checksStatus === 'ready') {
        this.issues = authoritativeCoverage ? this.issues.flatMap(issue => {
          const actions = issue.affectedReports ?? issue.reports;
          const remaining = actions.filter(r => !ids.has(String(r.id)));
          if (!remaining.length) return [];
          return [remaining.length === actions.length ? issue : { ...issue, affectedReports: remaining }];
        }) : this.issues.filter(issue => !issue.reports.every(r => ids.has(String(r.id))));
      }
      const byId = new Map(this.issues.map(issue => [issue.id, issue]));
      for (const issue of mergeIssueViews(issues)) {
        const previous = byId.get(issue.id);
        const unavailable = previous?.reports.filter(report => !ids.has(String(report.id))) || [];
        const unavailableActions = (previous?.affectedReports ?? previous?.reports ?? [])
          .filter(report => !ids.has(String(report.id)));
        byId.set(issue.id, unavailable.length
          ? { ...issue, reports: [...issue.reports, ...unavailable], fuel: undefined,
            ...(unavailableActions.length ? { affectedReports: [...(issue.affectedReports ?? issue.reports), ...unavailableActions] } : {}) }
          : issue);
      }
      this.issues = [...byId.values()];
      if (checksStatus !== 'unavailable' && this.issues.some(issue => issue.reports.some(report => !ids.has(String(report.id))))) checksStatus = 'paused';
      this.status.next(checksStatus);
      const cachedById = new Map(local.map(report => [String(report.id), report]));
      const needsMerge = incoming.some(report => {
        const cachedReport = cachedById.get(String(report.id));
        return !cachedReport ||
          (!!report.issueAccessToken && cachedReport.issueAccessToken !== report.issueAccessToken) ||
          (cachedReport.synced === true && (cachedReport.serverVersion !== report.version || cachedReport.locked !== report.locked));
      });
      if (incoming.length && (changed || needsMerge)) await this.reports.mergeSavedReports(ship.imo, incoming);
      if (epoch !== this.epoch) return;
      if (importToken && this.notifiedImport !== importToken && sessionStorage.getItem(historyImportKey(ship.uuid)) === importToken) {
        this.notifiedImport = importToken;
        const added = incoming.filter(r => !local.some(saved => String(saved.id) === String(r.id))).length;
        this.core.showSnackbar('success', !incoming.length ? 'No eligible reports found' : added ? `${added} ${added === 1 ? 'report imported' : 'reports imported'}` : 'Reports are up to date', '', { duration: 4000 });
      }
      if (epoch !== this.epoch) return;
      if (unavailableVoyages.size) await this.reports.forgetVoyages(ship.imo, [...unavailableVoyages]);
      this.changed.next();
    } catch {
      if (epoch === this.epoch) { this.status.next('unavailable'); this.changed.next(); }
    }
  }
}
